import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Mail, MessageSquare, ChevronRight, AlertTriangle, CheckCircle, Loader2, X } from 'lucide-react'
import api from '@/services/api'

const REASONS = [
  'I receive too many messages',
  'The content is not relevant to me',
  'I did not sign up for this',
  'I prefer to check deals myself',
  'Privacy concerns',
  'Other',
]

interface UnsubscribeInfo {
  token: string
  customer_name: string | null
  email: string | null
  mobile: string | null
  email_subscribed: boolean
  sms_subscribed: boolean
}

export default function Unsubscribe() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const typeHint = params.get('type') // 'email' or 'sms' — used to pre-select

  const [step, setStep] = useState(1)
  const [info, setInfo] = useState<UnsubscribeInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [unsubscribedList, setUnsubscribedList] = useState<string[]>([])

  // Step 2
  const [unsubEmail, setUnsubEmail] = useState(false)
  const [unsubSMS, setUnsubSMS] = useState(false)
  const [step2Error, setStep2Error] = useState('')

  // Step 3
  const [reason, setReason] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')
  const [step3Error, setStep3Error] = useState('')

  // Step 4
  const [confirmText, setConfirmText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [step4Error, setStep4Error] = useState('')

  useEffect(() => {
    if (!token) {
      setError('No unsubscribe token provided. Please use the link from your email or SMS.')
      setLoading(false)
      return
    }
    api.get(`/marketing/unsubscribe-info/${token}`)
      .then(r => {
        setInfo(r.data)
        // Pre-select based on type hint
        if (typeHint === 'email') setUnsubEmail(true)
        if (typeHint === 'sms') setUnsubSMS(true)
      })
      .catch(e => {
        setError(e?.response?.data?.detail || 'This unsubscribe link is invalid or has expired.')
      })
      .finally(() => setLoading(false))
  }, [token])

  const goToStep2 = () => setStep(2)

  const goToStep3 = () => {
    if (!unsubEmail && !unsubSMS) {
      setStep2Error('Please select at least one option to unsubscribe from.')
      return
    }
    setStep2Error('')
    setStep(3)
  }

  const goToStep4 = () => {
    if (!reason) {
      setStep3Error('Please select a reason before continuing.')
      return
    }
    setStep3Error('')
    setStep(4)
  }

  const handleConfirm = async () => {
    if (confirmText.trim().toUpperCase() !== 'UNSUBSCRIBE') {
      setStep4Error('Please type UNSUBSCRIBE exactly to confirm.')
      return
    }
    setStep4Error('')
    setSubmitting(true)
    try {
      const res = await api.post(`/marketing/process-unsubscribe/${token}`, {
        unsubscribe_email: unsubEmail,
        unsubscribe_sms: unsubSMS,
        reason,
        reason_detail: reasonDetail || null,
      })
      setUnsubscribedList(res.data.unsubscribed || [])
      setDone(true)
    } catch (e: any) {
      setStep4Error(e?.response?.data?.detail || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const totalSteps = 4

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center space-y-4">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <X className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Link Not Found</h1>
          <p className="text-gray-600 text-sm">{error}</p>
          <Link to="/" className="inline-block text-sm text-primary-600 hover:underline">Return to homepage</Link>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center space-y-4">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Preferences Updated</h1>
          <p className="text-gray-600 text-sm">
            You have been unsubscribed from:
          </p>
          <ul className="text-sm text-gray-700 space-y-1">
            {unsubscribedList.includes('email') && (
              <li className="flex items-center justify-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" /> Email marketing campaigns
              </li>
            )}
            {unsubscribedList.includes('sms') && (
              <li className="flex items-center justify-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-400" /> SMS marketing campaigns
              </li>
            )}
          </ul>
          <p className="text-xs text-gray-500">
            You will still receive important account-related messages such as order confirmations and billing notifications.
          </p>
          <Link to="/" className="inline-block text-sm text-primary-600 hover:underline">Return to homepage</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow w-full max-w-lg">

        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-t-xl px-6 py-5 text-white">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-gray-300">Step {step} of {totalSteps}</span>
          </div>
          {/* Step progress bar */}
          <div className="flex gap-1 mb-3">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all ${i < step ? 'bg-green-400' : 'bg-gray-500'}`}
              />
            ))}
          </div>
          <h1 className="text-lg font-bold">
            {step === 1 && 'Manage Your Marketing Preferences'}
            {step === 2 && 'What Would You Like to Unsubscribe From?'}
            {step === 3 && 'Help Us Improve — Tell Us Why'}
            {step === 4 && 'Final Confirmation'}
          </h1>
        </div>

        <div className="p-6 space-y-5">

          {/* ── STEP 1: Who you are ── */}
          {step === 1 && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  You are about to manage your marketing subscription preferences with Silux Mobile. This is a multi-step process — please read each step carefully.
                </p>
              </div>

              {info?.customer_name && (
                <p className="text-sm text-gray-700">
                  We have identified you as <strong>{info.customer_name}</strong>.
                </p>
              )}

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Your current subscriptions:</p>
                <div className="space-y-2">
                  {info?.email && (
                    <div className={`flex items-center gap-3 p-3 rounded-lg border ${info.email_subscribed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <Mail className={`w-5 h-5 ${info.email_subscribed ? 'text-green-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email Marketing</p>
                        <p className="text-xs text-gray-500">{info.email}</p>
                      </div>
                      <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${info.email_subscribed ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                        {info.email_subscribed ? 'Active' : 'Already off'}
                      </span>
                    </div>
                  )}
                  {info?.mobile && (
                    <div className={`flex items-center gap-3 p-3 rounded-lg border ${info.sms_subscribed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <MessageSquare className={`w-5 h-5 ${info.sms_subscribed ? 'text-green-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">SMS Marketing</p>
                        <p className="text-xs text-gray-500">{info.mobile}</p>
                      </div>
                      <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${info.sms_subscribed ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                        {info.sms_subscribed ? 'Active' : 'Already off'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-500">
                By continuing, you will go through a short process to select which communications you'd like to stop. You will still receive essential account and transactional messages.
              </p>

              <button
                onClick={goToStep2}
                className="w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Continue to Preferences <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* ── STEP 2: Choose what to unsubscribe from ── */}
          {step === 2 && (
            <>
              <p className="text-sm text-gray-600">
                Select the types of marketing you wish to stop receiving. You must select at least one.
              </p>

              <div className="space-y-3">
                {info?.email && info.email_subscribed && (
                  <label className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${unsubEmail ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="checkbox"
                      checked={unsubEmail}
                      onChange={e => setUnsubEmail(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-red-500"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold text-gray-900 text-sm">Email Marketing Campaigns</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Promotions, offers, and newsletters sent to {info.email}</p>
                    </div>
                  </label>
                )}
                {info?.mobile && info.sms_subscribed && (
                  <label className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${unsubSMS ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="checkbox"
                      checked={unsubSMS}
                      onChange={e => setUnsubSMS(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-red-500"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold text-gray-900 text-sm">SMS Marketing Campaigns</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Promotional text messages sent to {info.mobile}</p>
                    </div>
                  </label>
                )}
              </div>

              {step2Error && <p className="text-sm text-red-600">{step2Error}</p>}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={goToStep3}
                  className="flex-1 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors text-sm"
                >
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* ── STEP 3: Reason ── */}
          {step === 3 && (
            <>
              <p className="text-sm text-gray-600">
                Before we process your request, we'd like to understand why. Your feedback helps us improve our communications.
              </p>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Reason <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-1 gap-2">
                  {REASONS.map(r => (
                    <label
                      key={r}
                      className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm ${reason === r ? 'border-gray-700 bg-gray-50 font-medium' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <input
                        type="radio"
                        name="reason"
                        value={r}
                        checked={reason === r}
                        onChange={() => setReason(r)}
                        className="accent-gray-700"
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Additional comments (optional)</label>
                <textarea
                  value={reasonDetail}
                  onChange={e => setReasonDetail(e.target.value)}
                  rows={3}
                  placeholder="Tell us more..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-gray-500 focus:outline-none resize-none"
                />
              </div>

              {step3Error && <p className="text-sm text-red-600">{step3Error}</p>}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={goToStep4}
                  className="flex-1 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors text-sm"
                >
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* ── STEP 4: Final confirmation ── */}
          {step === 4 && (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-red-800">You are about to unsubscribe from:</p>
                <ul className="text-sm text-red-700 space-y-1 ml-2">
                  {unsubEmail && <li>• Email marketing campaigns</li>}
                  {unsubSMS && <li>• SMS marketing campaigns</li>}
                </ul>
                <p className="text-xs text-red-600 pt-1">
                  This action cannot be easily undone. You will no longer receive any offers, promotions, or news from Silux Mobile through the selected channels.
                </p>
              </div>

              <p className="text-sm font-medium text-gray-700">
                Reason: <span className="text-gray-900">{reason}{reasonDetail ? ` — ${reasonDetail}` : ''}</span>
              </p>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  To confirm, type <strong className="font-mono bg-gray-100 px-1 rounded">UNSUBSCRIBE</strong> in the box below:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="Type UNSUBSCRIBE"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-red-400 focus:outline-none font-mono"
                  autoComplete="off"
                />
              </div>

              {step4Error && <p className="text-sm text-red-600">{step4Error}</p>}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep(3)}
                  disabled={submitting}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={submitting || confirmText.trim().toUpperCase() !== 'UNSUBSCRIBE'}
                  className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors text-sm"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {submitting ? 'Processing...' : 'Confirm Unsubscribe'}
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Changed your mind? <Link to="/" className="text-primary-600 hover:underline">Return to homepage</Link>
              </p>
            </>
          )}

        </div>
      </div>
    </div>
  )
}
